import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Button, tokens } from '@hotjar/hotsauce';
import { useHelpHotjarUrl } from '@hotjar/translations';

import type { Integration } from '../../types';
import { GoogleAnalyticsActions } from './GoogleAnalyticsActions';
import { OptimizelyActions } from './OptimizelyActions';
import { GoogleOptimizeActions } from './GoogleOptimizeActions';
import { SlackActions } from './SlackActions';
import { ZapierActions } from './ZapierActions';
import { HubspotActions } from './HubspotActions';
import { MsTeamsActions } from './MsTeamsActions';
import { MixpanelActions } from './MixpanelActions';
import { UnbounceActions } from './UnbounceActions';
import { UpgradeToUnlock } from './UpgradeToUnlock';

const CTAWrapper = styled.div`
  display: block;
  margin-top: ${tokens.spacer.x1};
`;

const ActionsWrapper = styled.form`
  display: flex;
  flex-direction: column;
  margin-top: ${tokens.spacer.x1};
`;

interface IntegrationActionsProps {
  canUse: boolean | null;
  integration: Integration;
  isAdmin: boolean;
  siteId: string | null;
  siteName: string | null;
}

export const IntegrationActions = ({
  canUse,
  integration,
  isAdmin,
  siteId,
  siteName,
}: IntegrationActionsProps): JSX.Element => {
  const { t } = useTranslation();
  const helpHotjarUrl = useHelpHotjarUrl();

  if (!canUse) {
    return <UpgradeToUnlock siteName={siteName} integrationName={integration.name} />;
  }

  if (integration.slug === 'google-analytics') {
    return (
      <ActionsWrapper>
        <GoogleAnalyticsActions
          siteId={siteId}
          actionLabel={integration.actionLabel}
          isAdmin={isAdmin}
        />
      </ActionsWrapper>
    );
  }

  if (integration.slug === 'optimizely') {
    return (
      <ActionsWrapper>
        <OptimizelyActions
          siteId={siteId}
          actionLabel={integration.actionLabel}
          isAdmin={isAdmin}
        />
      </ActionsWrapper>
    );
  }

  if (integration.slug === 'google-optimize') {
    return (
      <ActionsWrapper>
        <GoogleOptimizeActions
          siteId={siteId}
          actionLabel={integration.actionLabel}
          isAdmin={isAdmin}
        />
      </ActionsWrapper>
    );
  }

  if (integration.slug === 'slack') {
    return (
      <CTAWrapper>
        <SlackActions
          siteId={siteId}
          actionLabel={integration.actionLabel}
          isAdmin={isAdmin}
          siteName={siteName}
        />
      </CTAWrapper>
    );
  }

  if (integration.slug === 'zapier') {
    return (
      <CTAWrapper>
        <ZapierActions />
      </CTAWrapper>
    );
  }

  if (integration.slug === 'hubspot') {
    return (
      <CTAWrapper>
        <HubspotActions siteId={siteId} actionLabel={integration.actionLabel} isAdmin={isAdmin} />
      </CTAWrapper>
    );
  }

  if (integration.slug === 'microsoft-teams') {
    return (
      <CTAWrapper>
        <MsTeamsActions
          siteId={siteId}
          siteName={siteName}
          actionLabel={integration.actionLabel}
          isAdmin={isAdmin}
        />
      </CTAWrapper>
    );
  }

  if (integration.slug === 'mixpanel') {
    return (
      <CTAWrapper>
        <MixpanelActions siteId={siteId} actionLabel={integration.actionLabel} isAdmin={isAdmin} />
      </CTAWrapper>
    );
  }

  if (integration.slug === 'unbounce') {
    return (
      <CTAWrapper>
        <UnbounceActions siteId={siteId} actionLabel={integration.actionLabel} isAdmin={isAdmin} />
      </CTAWrapper>
    );
  }

  if (
    integration.slug === 'segment' ||
    integration.slug === 'omniconvert' ||
    integration.slug === 'webhooks'
  ) {
    let parsedUrl = integration.installUrl;

    // update help.hotjar.com links with current language
    if (!parsedUrl?.includes('https://')) {
      parsedUrl = `${helpHotjarUrl}${parsedUrl}`;
    }

    return (
      <CTAWrapper>
        <Button icon="open_new_window" as="a" href={parsedUrl} isExternal kind="secondary">
          {t(integration.actionLabel.enable)}
        </Button>
      </CTAWrapper>
    );
  }

  return (
    <CTAWrapper>
      <Button kind={!integration.isEnabled ? 'primary' : 'danger_secondary'} isDisabled={!isAdmin}>
        {!integration.isEnabled
          ? t(integration.actionLabel.enable)
          : t(integration.actionLabel.disable)}
      </Button>
    </CTAWrapper>
  );
};
